/**
 * Hook to access user entitlements (features, limits, billing status).
 * 
 * Source of truth: /api/me
 * Cached in localStorage for offline fallback (1h TTL).
 */
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { Entitlements, Me } from "../types";

type UseEntitlementsResult = {
  entitlements: Entitlements | null;
  isPlatformAdmin: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const CACHE_KEY = "entitlements_cache";
const CACHE_TTL_MS = 3600_000; // 1 hour

type CachedMeProfile = {
  entitlements: Entitlements;
  isPlatformAdmin: boolean;
  timestamp: number;
};

function getCachedProfile(): CachedMeProfile | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedMeProfile | CachedEntitlementsLegacy;
    const age = Date.now() - cached.timestamp;

    if (age > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    if ("isPlatformAdmin" in cached) {
      return cached;
    }

    return {
      entitlements: (cached as CachedEntitlementsLegacy).data,
      isPlatformAdmin: false,
      timestamp: cached.timestamp,
    };
  } catch {
    return null;
  }
}

type CachedEntitlementsLegacy = {
  data: Entitlements;
  timestamp: number;
};

function setCachedProfile(entitlements: Entitlements, isPlatformAdmin: boolean): void {
  try {
    const cached: CachedMeProfile = {
      entitlements,
      isPlatformAdmin,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors (quota exceeded, private mode)
  }
}

export function useEntitlements(): UseEntitlementsResult {
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchEntitlements() {
    setLoading(true);
    setError(null);

    const result = await api.GET("/api/me");

    if (result.error) {
      const cached = getCachedProfile();
      if (cached) {
        setEntitlements(cached.entitlements);
        setIsPlatformAdmin(cached.isPlatformAdmin);
        setError(null);
      } else {
        setError("Nie udało się pobrać uprawnień.");
      }
      setLoading(false);
      return;
    }

    const data = result.data as Me;
    setEntitlements(data.entitlements);
    setIsPlatformAdmin(Boolean(data.is_platform_admin));
    setCachedProfile(data.entitlements, Boolean(data.is_platform_admin));
    setLoading(false);
  }

  useEffect(() => {
    const cached = getCachedProfile();
    if (cached) {
      setEntitlements(cached.entitlements);
      setIsPlatformAdmin(cached.isPlatformAdmin);
      setLoading(false);
    }

    void fetchEntitlements();
  }, []);

  return {
    entitlements,
    isPlatformAdmin,
    loading,
    error,
    refetch: fetchEntitlements,
  };
}

/**
 * Helper: check if feature is enabled.
 */
export function hasFeature(
  entitlements: Entitlements | null,
  feature: keyof Entitlements["features"]
): boolean {
  return entitlements?.features[feature] ?? false;
}

/**
 * Helper: get limit value (null = unlimited).
 */
export function getLimit(
  entitlements: Entitlements | null,
  limit: keyof Entitlements["limits"]
): number | null {
  return entitlements?.limits[limit] ?? null;
}
